import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'
import Select from 'react-select'
import Stats from './stats/Stats'
import Reports from './Reports'
import AutoReports from './AutoReports'
import Relayer from './Relayer'
import Notifications from './Notifications'
import Logs from './logs/Logs'
import AllMonitors from './AllMonitors'
import Settings from './settings/Settings'
import Overview from './overview/Overview'
import Tasks from './tasks/Tasks'
import Address from '../general/Address'
import useCrons from '../../hooks/useCrons'
import useEnvironment from '../../hooks/useEnvironment'
import { CHAIN_INFO } from '../../constants/networks'
import { parseName } from '../../utils/math-utils'
import useChainStats from '../../hooks/useChainStats'

interface DropdownOption {
  value: number
  label: string
}

type Params = {
  tab: string
  id: string
}

const TAB_LIST = [
  { id: 'overview', label: 'Overview' },
  { id: 'logs', label: 'Logs' },
  { id: 'allMonitors', label: 'Monitor' },
  { id: 'tasks', label: 'Tasks' },
  { id: 'stats', label: 'Stats' },
  { id: 'reports', label: 'Reports' },
  { id: 'autoReports', label: 'Auto-report' },
  { id: 'relayer', label: 'Relayer' },
  { id: 'notifications', label: 'Notifications' },
  { id: 'settings', label: 'Settings' },
]

const Tabs: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('overview')
  const [dropdown, setDropDown] = useState<DropdownOption[]>([])
  const [selectedOption, setSelectedOption] = useState<DropdownOption | null>(null)
  const params = useParams<Params>()
  const tab = params.tab

  const { data: crons } = useCrons()
  const { data: environmentData, isLoading: isEnvLoading } = useEnvironment(params.id)
  const { data: chainData, isLoading: isChainLoading } = useChainStats(selectedOption?.value)

  const isLoading = isEnvLoading || isChainLoading

  useEffect(() => {
    if (environmentData) {
      const smartVaults = environmentData?.smartVaults
      const uniqueChainIDs = new Set<number>()
      smartVaults.forEach((item) => {
        uniqueChainIDs.add(item.chainId)
      })

      const sortedUniqueChainIDs = Array.from(uniqueChainIDs).sort((a, b) => a - b)
      const chainObjects: DropdownOption[] = sortedUniqueChainIDs.map((chainId) => ({
        value: chainId,
        label: CHAIN_INFO[chainId] ? CHAIN_INFO[chainId].name : `Chain ${chainId}`,
      }))

      setSelectedOption(chainObjects[0])
      setDropDown(chainObjects)
    }
  }, [environmentData])

  useEffect(() => {
    if (tab) setActiveTab(tab)
  }, [tab])

  const navigate = useNavigate()

  const handleTabClick = (tab: string) => {
    setActiveTab(tab)
    navigate(`/dashboard/environments/${params.id}/${tab}`)
  }

  const keyToIndexMap = dropdown.reduce<Record<string, number>>((map, obj, index) => {
    map[(index + 1).toString()] = index
    return map
  }, {})

  const handleKeyDown = (event: KeyboardEvent) => {
    const index = keyToIndexMap[event.key]
    if (index !== undefined) handleMultiNetwork(dropdown[index])
  }

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [dropdown, keyToIndexMap])

  const handleMultiNetwork = (selectedOption?: DropdownOption) => {
    setSelectedOption(selectedOption || null)
  }

  const showAllNetworks = activeTab === 'relayer' || activeTab === 'allMonitors' || activeTab === 'notifications' || activeTab === 'reports' || activeTab === 'autoReports'

  return (
    <Page>
      <Header>
        <HeaderLeft>
          <PageTitle>
            {environmentData ? parseName(environmentData.namespace) : <Skeleton width={200} />}
          </PageTitle>
          {environmentData && (
            <PageMeta>
              <MetaText>{environmentData.namespace}</MetaText>
              <MetaDot>·</MetaDot>
              <Address
                address={environmentData.mimicId}
                short={true}
                showIdentity={false}
                chainId={selectedOption?.value}
              />
            </PageMeta>
          )}
        </HeaderLeft>

        <HeaderRight>
          {dropdown.length > 0 && (
            showAllNetworks ? (
              <AllNetworksBadge>All Networks</AllNetworksBadge>
            ) : (
              <ChainSelect
                classNamePrefix="chain"
                onChange={(opt) => handleMultiNetwork(opt as DropdownOption)}
                options={dropdown}
                value={selectedOption}
                defaultValue={dropdown?.length > 0 ? dropdown[0] : null}
                isSearchable={false}
              />
            )
          )}
        </HeaderRight>
      </Header>

      <TabBar>
        {TAB_LIST.map((t) => (
          <TabBtn
            key={t.id}
            active={activeTab === t.id}
            onClick={() => handleTabClick(t.id)}
          >
            {t.label}
          </TabBtn>
        ))}
      </TabBar>

      {isLoading && <LoaderBar />}

      {selectedOption && environmentData && (
        <Content>
          {chainData && activeTab === 'overview' && (
            <Overview chainId={selectedOption.value} chainInfo={chainData} crons={crons} />
          )}
          {activeTab === 'logs' && <Logs chainId={selectedOption.value} />}
          {activeTab === 'stats' && <Stats chainId={selectedOption.value} />}
          {activeTab === 'reports' && <Reports />}
          {activeTab === 'autoReports' && <AutoReports />}
          {activeTab === 'allMonitors' && <AllMonitors />}
          {activeTab === 'tasks' && (
            <Tasks chainId={selectedOption.value} smartVaults={environmentData.smartVaults} />
          )}
          {activeTab === 'settings' && (
            selectedOption ? (
              <Settings chainId={selectedOption.value} environment={environmentData} chainInfo={chainData || null} />
            ) : (
              <LoaderBar />
            )
          )}
          {activeTab === 'notifications' && <Notifications />}
          {activeTab === 'relayer' && <Relayer />}
        </Content>
      )}
    </Page>
  )
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const Page = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Inter', 'DMSans', sans-serif;
`

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 12px;
  }
`

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  padding-top: 4px;
  flex-shrink: 0;

  @media (max-width: 600px) {
    padding-top: 0;
  }
`

const PageTitle = styled.h1`
  color: #ededed;
  font-size: 20px;
  font-weight: 600;
  letter-spacing: -0.02em;
  margin: 0;
`

const PageMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #777;
  font-size: 12px;
  min-width: 0;
`

const MetaText = styled.span`
  color: #777;
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 11px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 600px) {
    max-width: 160px;
  }
`

const MetaDot = styled.span`
  color: #777;
`

const AllNetworksBadge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 4px;
  background: rgba(51, 194, 176, 0.08);
  border: 1px solid rgba(51, 194, 176, 0.2);
  color: #33C2B0;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.02em;
`

const ChainSelect = styled(Select)`
  .chain__control {
    min-width: 160px;
    background: #111;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 6px;
    box-shadow: none;
    cursor: pointer;
    min-height: 32px;
    height: 32px;
    flex-wrap: nowrap;

    &:hover {
      border-color: rgba(255, 255, 255, 0.14);
    }
  }

  .chain__value-container {
    padding: 0 10px;
  }

  .chain__single-value {
    color: #ededed;
    font-size: 13px;
  }

  .chain__indicator-separator {
    display: none;
  }

  .chain__dropdown-indicator {
    color: #a1a1a1;
    padding: 0 8px;
    &:hover { color: #888; }
  }

  .chain__menu {
    background: #161616;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 6px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
    overflow: hidden;
    z-index: 100;
  }

  .chain__menu-list {
    padding: 4px;
  }

  .chain__option {
    background: transparent;
    color: #a0a0a0;
    font-size: 13px;
    border-radius: 4px;
    padding: 6px 10px;
    cursor: pointer;

    &.chain__option--is-focused {
      background: rgba(255, 255, 255, 0.05);
      color: #ededed;
    }

    &.chain__option--is-selected {
      background: rgba(111, 92, 230, 0.15);
      color: #a89aff;
    }
  }
`

const TabBar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  margin-bottom: 0;
`

const TabBtn = styled.button<{ active: boolean }>`
  padding: 8px 14px;
  background: none;
  border: none;
  border-bottom: 2px solid ${({ active }) => active ? '#6f5ce6' : 'transparent'};
  margin-bottom: -1px;
  color: ${({ active }) => active ? '#ededed' : '#555'};
  font-size: 13px;
  font-weight: ${({ active }) => active ? '500' : '400'};
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  transition: color 0.15s, border-color 0.15s;

  &:hover {
    color: ${({ active }) => active ? '#ededed' : '#a0a0a0'};
  }
`

const loadingAnim = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(400%); }
`

const LoaderBar = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.04);
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 25%;
    height: 100%;
    background: linear-gradient(90deg, transparent, #6f5ce6, transparent);
    animation: ${loadingAnim} 1.2s ease-in-out infinite;
  }
`

const Skeleton = styled.div<{ width: number }>`
  width: ${({ width }) => width}px;
  height: 20px;
  border-radius: 4px;
  background: linear-gradient(90deg, #1a1a1a 25%, #222 50%, #1a1a1a 75%);
  background-size: 200% 100%;
`

const Content = styled.div`
  padding-top: 24px;
`

export default Tabs
